import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardResponseSchema, type CategoryStock, type DashboardUnit, type UnitStats } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';

/**
 * The commander's dashboard (REQUIREMENTS §2.9): hero numbers, a per-unit
 * table that adds up, the oldest unsigned forms, and — for the storeroom —
 * issued vs available by category. Counts only; every number links to the
 * screen that lists the things it counts.
 */
export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const state = useApi('/dashboard', dashboardResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;
  const { totals, units, unsignedForms, stock, generatedAt } = state.data;
  const locale = i18n.language.startsWith('en') ? 'en-GB' : 'he-IL';
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(locale) : '');
  const ageDays = (iso: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : 0);

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">{t('dashboard.title')}</h2>
          <span className="latin-inline text-xs text-slate-500">{t('dashboard.asOf', { when: new Date(generatedAt).toLocaleString(locale) })}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Tile label={t('dashboard.soldiers')} value={totals.soldiers} to="/org" testId="tile-soldiers" />
          <Tile label={t('dashboard.openItems')} value={totals.openItems} to="/org" testId="tile-open" />
          <Tile label={t('dashboard.unsignedForms')} value={totals.unsignedForms} to="/forms?status=pending_signature" tone={totals.unsignedForms > 0 ? 'warning' : undefined} testId="tile-unsigned" />
          <Tile label={t('dashboard.overdue')} value={totals.overdue} to="/alerts" tone={totals.overdue > 0 ? 'serious' : undefined} testId="tile-overdue" />
          <Tile label={t('dashboard.lostDamaged')} value={totals.lost + totals.damaged} to="/reports" tone={totals.lost + totals.damaged > 0 ? 'serious' : undefined} testId="tile-reports" />
          <Tile label={t('dashboard.inRotation')} value={totals.inRotation} to="/alerts" testId="tile-rotation" />
        </div>
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <h3 className="font-semibold text-slate-900">{t('dashboard.byUnit')}</h3>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                <th className="py-1 pe-3 text-start font-medium">{t('dashboard.unit')}</th>
                {(['soldiers', 'openItems', 'unsignedForms', 'overdue', 'lost', 'damaged', 'inRotation'] as const).map((k) => (
                  <th key={k} className="py-1 pe-3 text-end font-medium">{t(`dashboard.col.${k}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {units.map((u) => (
                <UnitRow key={u.unitId} unit={u} />
              ))}
              <tr className="font-semibold" data-testid="dashboard-totals">
                <td className="py-1.5 pe-3">{t('dashboard.total')}</td>
                <Cells stats={totals} />
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-baseline justify-between">
          <h3 className="font-semibold text-slate-900">{t('dashboard.unsignedTitle')}</h3>
          <Link to="/forms?status=pending_signature" className="text-xs text-slate-500 underline-offset-4 hover:underline">{t('dashboard.allForms')}</Link>
        </div>
        {unsignedForms.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500" data-testid="unsigned-empty">{t('dashboard.unsignedNone')}</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {unsignedForms.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm" data-testid="unsigned-form">
                <span>
                  <span className="font-medium text-slate-800">{f.soldierName}</span>
                  {f.unitName && <span className="ms-2 text-slate-500">{f.unitName}</span>}
                  <span className="ms-2 text-slate-500">{t('dashboard.lines', { count: f.lineCount })}</span>
                </span>
                <span className="flex items-center gap-3 text-xs text-slate-500">
                  <span className={ageDays(f.issuedAt) >= 7 ? 'text-amber-700' : undefined}>{t('dashboard.waiting', { count: ageDays(f.issuedAt), when: when(f.issuedAt) })}</span>
                  <Link to={`/forms/${f.id}`} className="text-slate-700 underline-offset-4 hover:underline">{t('alerts.openForm')}</Link>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {stock && (
        <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="dashboard-stock">
          <div className="flex items-baseline justify-between">
            <h3 className="font-semibold text-slate-900">{t('dashboard.stockTitle')}</h3>
            <Link to="/inventory" className="text-xs text-slate-500 underline-offset-4 hover:underline">{t('dashboard.fullInventory')}</Link>
          </div>
          <p className="mt-1 text-xs text-slate-500">{t('dashboard.stockHint')}</p>
          <ul className="mt-3 space-y-2">
            {stock.map((s) => (
              <StockBar key={s.category} row={s} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Tile({ label, value, to, tone, testId }: { label: string; value: number; to: string; tone?: 'warning' | 'serious'; testId: string }) {
  const ring = tone === 'serious' ? 'ring-red-200 bg-red-50' : tone === 'warning' ? 'ring-amber-200 bg-amber-50' : 'ring-slate-200 bg-slate-50';
  return (
    <Link to={to} className={`block rounded-xl p-3 ring-1 transition hover:ring-slate-400 ${ring}`} data-testid={testId}>
      <span className="block text-2xl font-semibold tabular-nums text-slate-900">{value}</span>
      <span className="block text-xs text-slate-600">{label}</span>
    </Link>
  );
}

function Cells({ stats }: { stats: UnitStats }) {
  const flag = (n: number, cls: string) => (n > 0 ? cls : 'text-slate-400');
  return (
    <>
      <td className="py-1.5 pe-3 text-end tabular-nums">{stats.soldiers}</td>
      <td className="py-1.5 pe-3 text-end tabular-nums">{stats.openItems}</td>
      <td className={`py-1.5 pe-3 text-end tabular-nums ${flag(stats.unsignedForms, 'text-amber-700')}`}>{stats.unsignedForms}</td>
      <td className={`py-1.5 pe-3 text-end tabular-nums ${flag(stats.overdue, 'text-red-700')}`}>{stats.overdue}</td>
      <td className={`py-1.5 pe-3 text-end tabular-nums ${flag(stats.lost, 'text-red-700')}`}>{stats.lost}</td>
      <td className={`py-1.5 pe-3 text-end tabular-nums ${flag(stats.damaged, 'text-red-700')}`}>{stats.damaged}</td>
      <td className={`py-1.5 pe-3 text-end tabular-nums ${flag(stats.inRotation, 'text-slate-700')}`}>{stats.inRotation}</td>
    </>
  );
}

function UnitRow({ unit }: { unit: DashboardUnit }) {
  const platoon = unit.type === 'platoon';
  return (
    <tr className={platoon ? 'bg-slate-50 font-medium' : undefined} data-testid="dashboard-unit" data-unit={unit.unitId}>
      <td className={`py-1.5 pe-3 whitespace-nowrap ${platoon ? '' : 'ps-4 text-slate-700'}`}>
        <Link to={`/org/${unit.unitId}`} className="underline-offset-4 hover:underline">{unit.name}</Link>
      </td>
      <Cells stats={unit} />
    </tr>
  );
}

/** One single-hue bar: the issued share of the category's total, labelled in text. */
function StockBar({ row }: { row: CategoryStock }) {
  const { t } = useTranslation();
  const out = row.issued + row.inRotation;
  const pct = row.total > 0 ? Math.round((out / row.total) * 100) : 0;
  return (
    <li data-testid="stock-row" data-category={row.category}>
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-slate-800">{row.category}</span>
        <span className="text-xs text-slate-600 tabular-nums">
          {t('dashboard.stockLine', { issued: out, available: row.available, total: row.total, pct })}
          {row.lost + row.damaged > 0 && <span className="ms-2 text-red-700">{t('dashboard.stockLost', { count: row.lost + row.damaged })}</span>}
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded bg-slate-200" role="img" aria-label={`${row.category}: ${pct}%`}>
        <div className="h-full rounded bg-slate-700" style={{ width: `${pct}%` }} />
      </div>
    </li>
  );
}
